import { useEffect, useRef, useState } from 'react'
import { Platform, View, ActivityIndicator, LogBox, Text, StyleSheet } from 'react-native'

// Suppress known React Native Web framework warning about text nodes (dev only)
// This occurs when toLocaleString('da-DK') formats numbers with '.' thousands separator
// inside nested Text components — RN Web briefly sees the '.' during reconciliation
LogBox.ignoreLogs(['Unexpected text node'])
if (__DEV__ && Platform.OS === 'web') {
  const origError = console.error
  console.error = (...args: any[]) => {
    if (typeof args[0] === 'string' && args[0].startsWith('Unexpected text node:')) return
    origError(...args)
  }
}
import { Stack, useRouter, SplashScreen } from 'expo-router'
import Head from 'expo-router/head'
import * as Updates from 'expo-updates'

// Keep splash screen visible until auth is resolved (prevents flicker)
SplashScreen.preventAutoHideAsync()

// Force-apply OTA updates on launch (before user interacts)
// Splash screen stays visible during this — no flicker
if (!__DEV__ && Platform.OS !== 'web') {
  Updates.checkForUpdateAsync()
    .then(({ isAvailable }) => {
      if (isAvailable) {
        return Updates.fetchUpdateAsync().then(() => Updates.reloadAsync())
      }
    })
    .catch((e) => console.warn('[OTA] Update check failed:', e?.message)) // Log but never crash
}
import { StatusBar } from 'expo-status-bar'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold } from '@expo-google-fonts/inter'
import { GestureHandlerRootView } from 'react-native-gesture-handler'
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet'
import { KeyboardProvider } from 'react-native-keyboard-controller'
import { DevAuthProvider } from '../src/auth/DevAuthProvider'
import { AuthProvider, useAuth } from '../src/auth/AuthProvider'
import { registerForPushNotifications, onNotificationTapped } from '../src/notifications'
import { getPostHog, identifyUser as phIdentify, initGlobalErrorHandlers } from '../src/lib/posthog'
import { initSentry, setSentryUser, Sentry } from '../src/lib/sentry'
import DevBanner from '../src/components/DevBanner'
import ToastProvider from '../src/components/Toast'
import { FeatureFlagsProvider } from '../src/context/FeatureFlagsContext'
import BetaWelcomeModal from '../src/components/BetaWelcomeModal'
import NPSModal from '../src/components/NPSModal'
import BiometricEnrollmentModal from '../src/components/BiometricEnrollmentModal'
import { persistentStorage } from '../src/lib/storageAdapter'
import * as Linking from 'expo-linking'
import { storage, ATTRIBUTION_CODE_KEY } from '../src/utils/storage'
import { colors } from '../src/theme'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
})

// Toggle this for dev mode (same pattern as web frontend Providers.tsx)
// Use EXPO_PUBLIC_DEV_MODE env var for deployed environments (Railway dev/staging),
// fall back to __DEV__ for local development
const railwayEnv = process.env.EXPO_PUBLIC_RAILWAY_ENV || ''
const IS_DEV = __DEV__ || process.env.EXPO_PUBLIC_DEV_MODE === 'true'

// Security check: EXPO_PUBLIC_RAILWAY_ENV guards against dev mode leaking to production
if (IS_DEV && railwayEnv === 'production') {
  throw new Error('SECURITY ERROR: DEV_MODE cannot be enabled in production build')
}

if (IS_DEV) {
  console.log('DEVELOPMENT MODE ACTIVE - Using mock authentication')
}

const Provider = IS_DEV ? DevAuthProvider : AuthProvider

/**
 * Inner layout that has access to auth context.
 * Handles push notification registration when user is authenticated.
 */
function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(true)
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      setIsOnline(navigator.onLine)
      const goOnline = () => setIsOnline(true)
      const goOffline = () => setIsOnline(false)
      window.addEventListener('online', goOnline)
      window.addEventListener('offline', goOffline)
      return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline) }
    }
  }, [])
  return isOnline
}

function InnerLayout() {
  const { user, isAuthenticated, isLoading } = useAuth()
  const router = useRouter()
  const pushRegistered = useRef(false)
  const isOnline = useOnlineStatus()

  // Initialize PostHog + Sentry + global error handlers
  useEffect(() => {
    getPostHog()
    initGlobalErrorHandlers()
    initSentry()
  }, [])

  // Capture tracking_code from deep links (Universal Links / QR visitkort)
  useEffect(() => {
    const handleDeepLink = (event: { url: string }) => {
      try {
        const parsed = Linking.parse(event.url)
        const code = parsed.queryParams?.tracking_code || parsed.queryParams?.utm_content
        if (code) storage.set(ATTRIBUTION_CODE_KEY, String(code)).catch(() => {})
      } catch {}
    }
    Linking.getInitialURL().then(url => { if (url) handleDeepLink({ url }) }).catch(() => {})
    const sub = Linking.addEventListener('url', handleDeepLink)
    return () => sub.remove()
  }, [])

  // Hide splash screen once auth state is resolved (prevents startup flicker)
  useEffect(() => {
    if (!isLoading) {
      SplashScreen.hideAsync()
    }
  }, [isLoading])

  // Inject web focus-visible styles for keyboard accessibility
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return
    const id = 'fa-focus-styles'
    if (document.getElementById(id)) return
    const style = document.createElement('style')
    style.id = id
    style.textContent = `
      *:focus-visible {
        outline: none;
        box-shadow: 0 0 0 2px ${colors.primary}33;
        border-radius: 4px;
      }
      *:focus:not(:focus-visible) {
        outline: none;
        box-shadow: none;
      }
    `
    document.head.appendChild(style)
  }, [])

  // Track cumulative app usage time (seconds) for NPS eligibility
  useEffect(() => {
    if (!isAuthenticated) return
    persistentStorage.load('app_usage_seconds')
    const interval = setInterval(() => {
      const cur = parseInt(persistentStorage.get('app_usage_seconds') || '0', 10)
      persistentStorage.set('app_usage_seconds', String(cur + 60))
    }, 60_000)
    return () => clearInterval(interval)
  }, [isAuthenticated])

  // Track session count (cold starts) for feedback grace period
  useEffect(() => {
    if (!isAuthenticated) return
    const key = 'app_session_count'
    persistentStorage.load(key).then(() => {
      const count = parseInt(persistentStorage.get(key) || '0', 10)
      persistentStorage.set(key, String(count + 1))
    })
  }, [isAuthenticated])

  // Identify user in PostHog + Sentry + register push token
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      phIdentify(user.id, { name: user.name, email: user.email })
      setSentryUser(user.id)
      if (!pushRegistered.current && Platform.OS !== 'web') {
        pushRegistered.current = true
        registerForPushNotifications(user.id).catch((err) => {
          console.warn('[PushNotif] Registration failed:', err?.message || err)
        })
      }
    }
  }, [isAuthenticated, user?.id])

  // Handle notification tap → navigate to relevant screen
  useEffect(() => {
    if (Platform.OS === 'web') return

    const sub = onNotificationTapped((response) => {
      const data = response.notification.request.content.data
      if (data?.screen === 'inbox') {
        if (data?.provider_id) {
          router.push(`/thread/${data.provider_id}` as any)
        } else {
          router.push('/(tabs)/inbox')
        }
      } else if (data?.screen === 'quotes') {
        router.push('/(tabs)/quotes')
      } else if (data?.screen === 'chat') {
        router.push('/(tabs)/chat')
      }
    })

    return () => sub.remove()
  }, [router])

  return (
    <BottomSheetModalProvider>
      <View style={{ flex: 1 }}>
        <DevBanner />
        {!isOnline && (
          <View style={offlineStyles.banner}>
            <Text style={offlineStyles.text}>Du er offline — tjek din forbindelse</Text>
          </View>
        )}
        <StatusBar style="dark" />
        <View style={{ flex: 1 }}>
          <Stack screenOptions={{ headerShown: false }}>
            <Stack.Screen name="(auth)" />
            <Stack.Screen name="(tabs)" options={{ gestureEnabled: false }} />
            <Stack.Screen name="onboarding/index" options={{ gestureEnabled: false }} />
            <Stack.Screen name="mitid/callback" options={{ headerShown: false }} />
            <Stack.Screen name="privatlivspolitik" options={{ headerShown: false }} />
            <Stack.Screen name="vilkaar" options={{ headerShown: false }} />
            <Stack.Screen
              name="thread/[id]"
              options={{
                headerShown: true,
                headerTitle: 'Besked',
                presentation: 'card',
              }}
            />
          </Stack>
        </View>
        <ToastProvider />
      </View>
    </BottomSheetModalProvider>
  )
}

function RootLayout() {
  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
  })

  if (!fontsLoaded) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#ffffff' }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    )
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      {Platform.OS === 'web' && (
        <Head>
          <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no" />
        </Head>
      )}
      <KeyboardProvider>
        <Provider>
          <QueryClientProvider client={queryClient}>
            <FeatureFlagsProvider>
              <InnerLayout />
              <BetaWelcomeModal />
              <NPSModal />
              <BiometricEnrollmentModal />
            </FeatureFlagsProvider>
          </QueryClientProvider>
        </Provider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  )
}

export default Sentry ? Sentry.wrap(RootLayout) : RootLayout

const offlineStyles = StyleSheet.create({
  banner: {
    backgroundColor: colors.primary,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  text: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
})
